// Signal de módulo (per-tab, en memoria) para la encuesta de PERMANENCIA — la que pedimos al
// ADOPTAR la versión nueva (pasivo: menu_state='adopted'; explícito: CTA "Quedarme").
//
// POR QUÉ existe (mismo patrón que revertFeedback.ts): la encuesta debe vivir DESACOPLADA del
// ciclo de vida del chip/popover que la dispara. El CTA "Quedarme" vive en el chip de status
// (página treatment); quien RENDERIZA la encuesta es el shell del menú (MenuVersionInvitation),
// que persiste aunque la página treatment se desmonte. El chip solo "pide" mostrarla.
//
// Dos disparadores convergen en el mismo signal:
//   - PASIVO: al resolver menu_state='adopted', el hook pide la encuesta UNA sola vez por
//     (comercio, feature) usando un guard DURABLE (Set de módulo, mismo patrón que
//     invitation_shown) para que no reaparezca tras remount/navegación.
//   - EXPLÍCITO: el CTA "Quedarme con la versión nueva" la pide tras enqueuear el switch 'kept'.
//
// NO se persiste el "pedido" (es un prompt de sesión). El guard del disparador pasivo tampoco se
// persiste: vive en memoria del tab y se vacía al cambiar de sesión/sucursal (resetPermanenceSurveyState).

type Listener = () => void;

// true = hay una encuesta de permanencia pendiente de mostrar para esta feature.
const requested = new Map<string, boolean>();
const listeners = new Map<string, Set<Listener>>();

// Guard DURABLE del disparador PASIVO, keyed por (comercio, feature): garantiza que el
// menu_state='adopted' abra la encuesta a lo sumo una vez por sesión, sobreviviendo a los
// remounts del shell (navegar entre subpáginas del menú). Mismo patrón que invitationShownFor.
const passiveShownFor = new Set<string>();

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

export function isPermanenceSurveyRequested(featureKey: string): boolean {
  return requested.get(featureKey) === true;
}

export function subscribePermanenceSurvey(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// Pide mostrar la encuesta (omitible, fire-and-forget). Idempotente: si ya está pedida no
// re-notifica. Lo llaman tanto el CTA "Quedarme" como el disparador pasivo.
export function requestPermanenceSurvey(featureKey: string): void {
  if (requested.get(featureKey) === true) return;
  requested.set(featureKey, true);
  notify(featureKey);
}

// El shell lo llama al enviar el motivo, omitir/cerrar la encuesta, u optar por no volver.
export function clearPermanenceSurvey(featureKey: string): void {
  if (requested.get(featureKey) !== true) return;
  requested.set(featureKey, false);
  notify(featureKey);
}

// Disparador PASIVO con guard durable. Devuelve true si DISPARÓ la encuesta en esta llamada
// (primera vez para ese comercio+feature); false si ya se mostró antes (no reabre). Sin commerce
// activo no dispara ni marca el guard (la telemetría iría sin sujeto). El caller decide cuándo
// invocarlo (al detectar menu_state='adopted' tras la hidratación).
export function maybeRequestPermanenceSurveyForAdopted(featureKey: string, commerceId: string | null): boolean {
  if (!commerceId) return false;
  const key = `${commerceId}:${featureKey}`;
  if (passiveShownFor.has(key)) return false;
  passiveShownFor.add(key);
  requestPermanenceSurvey(featureKey);
  return true;
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal ni un prompt pendiente ni el guard pasivo
// del usuario/comercio anterior deben sobrevivir. Coherente con resetRevertFeedbackState y el
// resto de resets del módulo (store/hydration/draining/preview/roleRejection/invitationShown).
export function resetPermanenceSurveyState(): void {
  const keys = Array.from(requested.keys());
  requested.clear();
  passiveShownFor.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetPermanenceSurveyForTests(): void {
  requested.clear();
  listeners.clear();
  passiveShownFor.clear();
}
